import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, MultiSelect } from '@/common';
import { queryClient } from '@/configuration/react-query';
import { PAGE_SIZE } from '@/constants';
import { COLORS } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { VideoThemeIcon } from '@/resources';
import { useGetAllVideoTheme } from '@/services';
import { useGetStockClipByAssetIdQuery, useUpdateStockClip } from '@/services/stockClip.service';
import { Asset, SelectOptionProps } from '@/types';
import { EditStockClipPayload } from '@/types/StockClipTypes';

type StockClipProps = {
  asset: Asset;
  isModalOpen: boolean;
  onClose: () => void;
};

export const StockClipDetails = ({ asset, isModalOpen, onClose }: StockClipProps) => {
  const [t] = useTranslation('common');

  const [selectedThemes, setSelectedThemes] = useState<SelectOptionProps[]>();
  const [searchVideoTheme, setSearchVideoTheme] = useState<string>('');

  const {
    isLoading,
    data: allThemes,
    fetchNextPage,
    hasNextPage
  } = useGetAllVideoTheme(PAGE_SIZE, searchVideoTheme);

  const { mutate: updateStockClip } = useUpdateStockClip(queryClient, {
    onSuccess: () => {
      onClose();
      showToast(ToastType.SUCCESS, t('stockClipSuccessfullyUpdated'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('stockClipUpdateError'));
    }
  });

  const { data: stockClip } = useGetStockClipByAssetIdQuery(Number(asset.id));

  const getThemeOptions = () => {
    return (
      allThemes?.pages?.flatMap(
        (page) =>
          page?.data?.items?.map((theme: { id: number; name: string }) => ({
            value: theme.id,
            label: t(theme.name)
          })) ?? []
      ) ?? []
    );
  };

  const handleUpdate = (selectedThemes: SelectOptionProps[]) => {
    const themeIds = selectedThemes?.map((theme) => theme?.value) || [];
    const payload: EditStockClipPayload = {
      id: stockClip?.data.id,
      videoThemeIds: themeIds as number[]
    };

    updateStockClip(payload);
  };

  const handleEditButton = () => {
    const relatedThemes = stockClip?.data.videoThemes.map((theme: { id: number }) => theme.id);
    const themesToRelate = selectedThemes?.map((item) => item?.value);
    if (relatedThemes) {
      const equal =
        relatedThemes.length === themesToRelate?.length &&
        relatedThemes.every((id: number) => themesToRelate?.includes(id));
      return equal;
    }
    return true;
  };

  const handleThemesRemove = (eventType: SelectOptionProps) => {
    const updatedThemes = selectedThemes?.filter((t) => t?.value !== eventType.value) || [];
    setSelectedThemes(updatedThemes);
  };

  useEffect(() => {
    if (stockClip?.data?.videoThemes) {
      const themes = stockClip.data.videoThemes.map((theme: { id: number; name: string }) => ({
        value: theme.id,
        label: theme.name
      }));
      setSelectedThemes(themes);
    }
  }, [stockClip]);

  return (
    <>
      <Modal
        height={'h-full'}
        label={t('clipPreview')}
        labelClasses='w-full text-xl mt-2 font-bold text-primary-blue'
        modalClasses='max-w-[100vh] w-auto border border-secondary-blue-gray px-1'
        containerClasses='no-scrollbar'
        hasButton
        hasCancelButton
        buttonText={t('save')}
        onClose={onClose}
        isOpen={isModalOpen}
        buttonClasses='flex mt-3 justify-end'
        handleSubmit={() => handleUpdate(selectedThemes as SelectOptionProps[])}
        isButtonDisabled={handleEditButton()}>
        <div className='flex flex-col space-y-3'>
          <MultiSelect
            className='mb-1 w-full'
            label={t('selectTheme')}
            id='stockClipTag'
            placeholder={t('themes')}
            options={isLoading ? [] : getThemeOptions()}
            handleChange={setSelectedThemes}
            selectedValues={selectedThemes ?? []}
            textColor={COLORS.PRIMARY_BLACK}
            borderStyle={`1px solid ${COLORS.PRIMARY_BLUE}`}
            fontWeight='400'
            dropdownIndicatorColor={COLORS.PRIMARY_BLUE}
            controlShouldRenderValue={false}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            handleInputChange={setSearchVideoTheme}
            tagIcon={VideoThemeIcon}
            showTags
            handleTagRemove={handleThemesRemove}
          />
          <video
            controls
            src={asset?.assetUrl}
            className='mb-3 flex h-[400px] w-full justify-center'
          />
        </div>
      </Modal>
    </>
  );
};
